import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'
import { updateSetupSession } from '../sessions'
import { SportTypeValues } from '../sports/data_types'
import { pitchSetupTypes, PitchSetupTypeValues } from './data_types'
import { PitchesState, PitchTypes } from './types'

export const getEmptyPitch = (sport: SportTypeValues) => {
  // Empty-pitch placeholder; a plain object can't be a concrete pitch class,
  // so it can't satisfy the PitchTypes union structurally.
  // TODO: revisit cast.
  return {
    id: '0',
    sport,
    pitch: null,
    name: 'Empty Pitch',
    arcs: [],
    anchors: {
      0: {
        id: 0,
        pos: { x: 0, y: 0, z: 0 },
        encodedId: '',
        serial: '0',
        index: 0
      }
    }
  } as unknown as PitchTypes
}

export const initialPitchesState: PitchesState = {
  pitchType: 0,
  inUse: null,
  side: 0,
  master: 0,
  anchorBump: 1.5,
  autoLocateActive: false,
  isAutoSetup: true,
  setupTagList: [null, null, null],
  secondPitchSetupTagList: [null, null, null],
  pitchJson: '',
  anchorJson: '',
  setupType: pitchSetupTypes.items.auto.value,
  doublePitchSetup: false,
  demonstrationPitch: false
}

export const pitchesSlice = createSlice({
  name: 'pitches',
  initialState: initialPitchesState,
  reducers: {
    setPitchInUse: (state, action: PayloadAction<PitchTypes>) => {
      const data = action.payload
      return {
        ...state,
        inUse: data,
        master: null,
        anchorBump: 1.5,
        autoLocateActive: false
      }
    },
    setAutoLocateActive: (state, action: PayloadAction<boolean>) => {
      const autoLocateActive = action.payload
      return {
        ...state,
        autoLocateActive: autoLocateActive
      }
    },
    setSetupType: (state, action: PayloadAction<PitchSetupTypeValues>) => {
      const setupType = action.payload
      return {
        ...state,
        setupType: setupType,
        side: setupType === pitchSetupTypes.items.pitch.value ? 0 : state.side
      }
    },
    updateTagList: (
      state,
      action: PayloadAction<{ index: number; tagId: string | null }>
    ) => {
      state.setupTagList[action.payload.index] = action.payload.tagId
    },
    updateSecondPitchTagList: (
      state,
      action: PayloadAction<{ index: number; tagId: string | null }>
    ) => {
      state.secondPitchSetupTagList[action.payload.index] = action.payload.tagId
    },
    setDoublePitchSetup: (state, action: PayloadAction<boolean>) => {
      const doublePitchSetup = action.payload
      return {
        ...state,
        doublePitchSetup: doublePitchSetup
      }
    },
    setDemonstrationPitch: (state, action: PayloadAction<boolean>) => {
      const demonstrationPitch = action.payload
      return {
        ...state,
        demonstrationPitch: demonstrationPitch
      }
    }
  }
})

export const actions = pitchesSlice.actions
export const reducer = pitchesSlice.reducer

export const {
  setPitchInUse,
  setAutoLocateActive,
  setSetupType,
  updateTagList,
  updateSecondPitchTagList,
  setDoublePitchSetup,
  setDemonstrationPitch
} = actions

export const handleSetPitchInUse =
  (pitch: PitchTypes | null) => (dispatch: any, getState: () => RootState) => {
    // Set empty pitch if no data is provided
    if (!pitch) {
      pitch = getEmptyPitch(getState().sports.selectedSportType)
    } else {
      // Ensure pitch is not null
      // TODO: check if this is necessary
      if (!pitch.pitch) {
        pitch.pitch = getEmptyPitch(getState().sports.selectedSportType).pitch
      }
    }

    dispatch(setPitchInUse(pitch))
    dispatch(
      updateSetupSession({
        pitch,
        pitchId: pitch.id
      })
    )
  }
